import { ReactNode, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import { PrimaryButton } from 'shared/components/button'
import FormInputRhf from 'shared/components/form-input-rhf'
import Modal from 'shared/components/modal'
import { EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import {
  isEmbeddedVideoUrlTooLong,
  isValidEmbeddedVideoUrl,
} from 'modules/attachments/utils/attachment-utils'

export interface SelectUrlModalProps {
  isOpen: boolean
  onSuccess: (url: string) => void | Promise<void>
  onClose: () => void
}

interface SelectUrlModalFormValues {
  url: string
}

const SelectUrlModal = ({ isOpen, onSuccess, onClose }: SelectUrlModalProps): ReactNode => {
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState(false)
  const form = useForm<SelectUrlModalFormValues>({
    mode: 'onChange',
    defaultValues: {
      url: '',
    },
  })

  const handleClose = (): void => {
    form.reset()
    onClose()
  }

  const handleSubmit: SubmitHandler<SelectUrlModalFormValues> = async ({ url }) => {
    setIsFetching(true)
    const normalizedUrl = url.trim()

    try {
      await onSuccess(normalizedUrl)
      form.reset()
      onClose()
    } finally {
      setIsFetching(false)
    }
  }

  const urlError = form.formState.errors.url?.message
  const submitForm = form.handleSubmit(handleSubmit)

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={t('post.attachments.embedded_video_link.title')}
      small
      isFetching={isFetching}
    >
      <div className="flex flex-col gap-6 lg:gap-10">
        <div className="flex flex-col justify-center gap-3">
          <FormInputRhf
            control={form.control}
            name="url"
            setValue={form.setValue}
            label={t('global.url')}
            type="url"
            error={urlError}
            onKeyDown={e => {
              if (e.key !== 'Enter') {
                return
              }

              e.preventDefault()
              void submitForm()
            }}
            rules={{
              validate: value => {
                const normalizedUrl = value.trim()

                if (isEmbeddedVideoUrlTooLong(normalizedUrl)) {
                  return t('post.attachments.embedded_video_link.error_too_long', {
                    max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
                  })
                }

                if (!isValidEmbeddedVideoUrl(normalizedUrl)) {
                  return t('post.attachments.embedded_video_link.error_unsupported')
                }

                return true
              },
            }}
          />
          <span>{t('post.attachments.embedded_video_link.description')}</span>
        </div>
        <div className="flex justify-center gap-4">
          <PrimaryButton isLoading={isFetching} onClick={submitForm} type="button">
            {t('confirmation_modal.confirm')}
          </PrimaryButton>
          <PrimaryButton
            onClick={e => {
              e.preventDefault()
              handleClose()
            }}
            disabled={isFetching}
            type="button"
          >
            {t('confirmation_modal.cancel')}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}

export default SelectUrlModal
